import { LoadingOverlay } from "@/components/LoadingOverlay";
import { LoadingSmall } from "@/components/LoadingSmall";
import { AppSelect, Option } from "@/components/ui/app-select";
import { getFirm, getUserDetails, getUserFirm } from "@/sideEffects/api";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserSystemDynamicSettingsRaw } from "./UserSystemDynamicSettingsRaw";

interface Props {
  userId: number;
}

export const UserSystemDynamicSettings: React.FC<Props> = ({ userId }) => {
  const { t } = useTranslation();
  const [selectedFirmId, setSelectedFirmId] = useState<string>();

  const { data: userDetails, isLoading } = useQuery({
    queryKey: ["getUserDetails", userId],
    queryFn: () => getUserDetails(userId),
  });

  const { data: userFirms, isLoading: isUserFirmsLoading } = useQuery({
    queryKey: ["getUserFirm", userId],
    queryFn: () => getUserFirm(userId),
  });

  const { data: allFirms, isLoading: isAllFirmsLoading } = useQuery({
    queryKey: ["getFirms"],
    queryFn: () =>
      getFirm({
        numberOfObjectsPerPage: 1000,
        pageNumber: 1,
      }),
  });

  if (!userDetails || isLoading) {
    return <LoadingSmall />;
  }

  const firmOptions: Option[] = (userFirms?.data || []).map((firm) => ({
    value: allFirms?.data.result.find((f) => f.id === firm.firm_Id)?.firmGuid || "",
    label: allFirms?.data.result.find((f) => f.id === firm.firm_Id)?.name || "",
  }));

  return (
    <LoadingOverlay isLoading={isUserFirmsLoading || isAllFirmsLoading}>
      <div className="w-[400px] mb-4">
        <AppSelect
          options={firmOptions}
          value={selectedFirmId}
          onChange={(value) => setSelectedFirmId(String(value))}
          placeholder={t("Firm")}
        />
      </div>
      {selectedFirmId ? (
        <UserSystemDynamicSettingsRaw userId={userDetails.data.guid} selectedFirmId={selectedFirmId} />
      ) : null}
    </LoadingOverlay>
  );
};
